import { Maybe } from "graphql/jsutils/Maybe";
import { Record } from "immutable";
import {
  EventsFromServer,
  EventsFromStorefront,
  ReceiveChatMessageRoute,
} from "mt-shared/socket-server/routes";
import React from "react";
import { Action as ReduxAction } from "redux";
import { Socket } from "socket.io-client";
import actionCreatorFactory from "typescript-fsa";
import { reducerWithInitialState } from "typescript-fsa-reducers";

namespace SocketRedux {
  export type ClientSocket = Socket<EventsFromServer, EventsFromStorefront>;

  export class State extends Record<{
    socket?: null | ClientSocket;
    joinedRoom?: boolean | null;
    receivedNewMessages: Maybe<Array<ReceiveChatMessageRoute["params"]>>;
  }>({
    socket: null,
    joinedRoom: null,
    receivedNewMessages: null,
  }) {}

  export const initialState: State = new State();

  const actionCreator = actionCreatorFactory();

  export const Action = {
    setSocket: actionCreator<null | ClientSocket | undefined>("SET_SOCKET"),
    joinRoom: actionCreator("JOIN_ROOM"),
    leaveRoom: actionCreator("LEAVE_ROOM"),
    receivedNewMessages: actionCreator<ReceiveChatMessageRoute["params"]>(
      "RECEIVED_NEW_MESSAGES"
    ),
  } as const;
  export type Action = (typeof Action)[keyof typeof Action];
  export type ActionType = Action["type"];

  export const reducer = reducerWithInitialState<State>(new State())
    .case(Action.setSocket, (state, payload) => {
      return state.set("socket", payload);
    })
    .case(Action.joinRoom, (state) => {
      return state.set("joinedRoom", true);
    })
    .case(Action.leaveRoom, (state) => {
      return state.set("joinedRoom", false);
    })
    .case(Action.receivedNewMessages, (state, payload) => {
      return state.set("receivedNewMessages", [
        ...(state.receivedNewMessages ?? []),
        payload,
      ]);
    });

  export type Reducer = typeof reducer;

  export type Context = {
    state: SocketRedux.State;
    dispatch: React.Dispatch<ReduxAction<ActionType>>;
  };

  export const Context = React.createContext({} as Context);

  export const useReducer = () => {
    const [state, dispatch] = React.useReducer(
      SocketRedux.reducer,
      SocketRedux.initialState
    );
    return {
      state,
      dispatch,
    };
  };

  export const useContext = () => {
    const context = React.useContext(SocketRedux.Context);
    return context;
  };
}

export default SocketRedux;
